import { useState, useRef, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { articles, categories, hotArticles, latestArticles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '黑料网在线',
  url: 'https://heiliao.online',
  description: '黑料网在线为您提供最新、最全的网络爆料内容，实时聚合热点黑料资讯，涵盖娱乐圈、网红主播、社会事件等多领域独家内幕。',
  foundingDate: '2024-01-01',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'contact@heiliao.online',
    availableLanguage: 'Chinese',
  },
};

const webSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '黑料网在线',
  url: 'https://heiliao.online',
  description: '黑料网在线为您提供最新、最全的网络爆料内容，实时聚合热点黑料资讯。',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://heiliao.online/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const SUBSCRIBE_FORM_URL = 'https://readdy.ai/api/form/d9qshsnnahb7382ovev0';

export default function Home() {
  const featuredArticle = articles.find(a => a.id === '1') || articles[0];
  const restHotArticles = hotArticles.filter(a => a.id !== featuredArticle.id).slice(0, 4);
  const [subStatus, setSubStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [subError, setSubError] = useState('');
  const subFormRef = useRef<HTMLFormElement>(null);

  const handleSubscribe = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubStatus('success');
      setSubError('');
      return;
    }
    try {
      const fd = new FormData(form);
      if (honeypot === undefined || honeypot === '') {
        // already handled above, but for consistency
      }
      const res = await fetch(SUBSCRIBE_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setSubStatus('success');
        setSubError('');
        form.reset();
      } else {
        const msg = (parsed?.meta as Record<string, string>)?.message || (parsed?.message as string) || responseText;
        setSubError(typeof msg === 'string' ? msg : '订阅失败，请稍后再试');
        setSubStatus('error');
      }
    } catch {
      setSubError('网络错误，请检查连接后重试');
      setSubStatus('error');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webSiteSchema) }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative h-[520px] md:h-[620px] flex items-center">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20dramatic%20abstract%20composition%20with%20deep%20red%20and%20charcoal%20tones%2C%20subtle%20newsprint%20texture%2C%20mysterious%20atmosphere%2C%20editorial%20photography%20style%2C%20low%20key%20lighting%2C%20modern%20elegant%20aesthetic&width=1600&height=800&seq=hero-bg-home&orientation=landscape"
            alt="黑料网在线"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
          <div className="container-custom relative z-10 w-full">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 text-xs font-semibold bg-primary-500 text-background-50 rounded-full whitespace-nowrap">
                  今日重磅
                </span>
                <span className="text-sm text-background-200/90">
                  {new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              </div>
              <h1 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl text-background-50 leading-tight mb-4">
                {featuredArticle.title}
              </h1>
              <p className="text-sm md:text-base text-background-200/80 leading-relaxed mb-6 max-w-xl">
                {featuredArticle.excerpt}
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <Link
                  to={`/article/${featuredArticle.id}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer"
                >
                  阅读全文
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
                <Link
                  to={`/category/${featuredArticle.categorySlug}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-50/15 hover:bg-background-50/25 backdrop-blur-sm text-background-50 text-sm font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer border border-background-50/20"
                >
                  {featuredArticle.category}
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="container-custom py-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/' : `/category/${cat.slug}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-sm`}></i>
                  </span>
                  {cat.name}
                  {cat.slug !== 'all' && (
                    <span className="text-xs text-foreground-400 ml-0.5">({cat.count})</span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="container-custom">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <div>
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">最新爆料</h2>
                <p className="text-sm text-foreground-500 mt-1">每日更新，独家内幕一手掌握</p>
              </div>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部
                <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {latestArticles.slice(0, 6).map((article) => (
                <ArticleCard key={article.id} article={article} variant="default" />
              ))}
            </div>
          </div>
        </section>

        {/* Hot Articles */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="container-custom">
            <div className="flex items-center gap-3 mb-6 md:mb-8">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-fire-fill text-xl text-accent-500"></i>
              </div>
              <div>
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">热门爆料</h2>
                <p className="text-sm text-foreground-500 mt-1">网友们都在看</p>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
              {restHotArticles.slice(0, 4).map((article) => (
                <ArticleCard key={article.id} article={article} variant="compact" />
              ))}
            </div>
          </div>
        </section>

        {/* Trending Topics */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="container-custom">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-6 md:mb-8">热门标签</h2>
            <div className="flex flex-wrap gap-3">
              {['娱乐圈', '偷税漏税', '食品安全', '网红主播', '数据造假', '绯闻', '反腐', '直播带货', '选秀黑幕', '隐私泄露', '代工厂', '电竞内讧'].map((tag) => (
                <button
                  key={tag}
                  className="px-4 py-2 text-sm font-medium rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap border border-background-200/50"
                >
                  #{tag}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Subscribe CTA */}
        <section className="py-12 md:py-16 bg-background-950">
          <div className="container-custom text-center">
            <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-mail-send-line text-2xl text-primary-500"></i>
            </div>
            <h2 className="font-heading font-bold text-xl md:text-2xl text-background-50 mb-3">
              不错过任何爆料
            </h2>
            <p className="text-sm text-background-400 mb-6 max-w-md mx-auto">
              订阅黑料网在线，第一时间获取最新独家内幕，做朋友圈里消息最灵通的那个人。
            </p>
            <form
              ref={subFormRef}
              onSubmit={handleSubscribe}
              data-readdy-form=""
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <input
                type="email"
                name="email"
                placeholder="输入你的邮箱地址"
                className="flex-1 px-4 py-3 text-sm rounded-md bg-background-800 text-background-50 placeholder:text-background-500 border border-background-700 focus:outline-none focus:border-primary-500 transition-colors"
                required
              />
              <input
                type="text"
                name="website_alt"
                className="ab-form-hp"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <button
                type="submit"
                className="px-6 py-3 text-sm font-medium bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                立即订阅
              </button>
            </form>
            {subStatus === 'success' && (
              <p className="text-sm text-green-400 mt-3">订阅成功！感谢你的关注。</p>
            )}
            {subStatus === 'error' && (
              <p className="text-sm text-red-400 mt-3">{subError}</p>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}